import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { MusicIcon } from '../../components/shared';
import { useTranslation } from 'react-i18next';

const Dashboard: React.FC = () => {
  const { user, artist } = useAuth();
  const { releases, getTotalEarnings, isLoading } = useData();
  const { t } = useTranslation();

  // Admin ise tüm release'ları göster, artist ise sadece kendi release'larını
  const userReleases = user?.role === 'admin' 
    ? releases 
    : releases.filter(release => release.artist_id === user?.id);
  
  const pendingReleases = userReleases.filter(release => release.status === 'pending' || release.status === 'submitted');
  const publishedReleases = userReleases.filter(release => release.status === 'published' || release.status === 'approved');
  const totalEarnings = getTotalEarnings();

  // Boş durumları kontrol et
  const hasReleases = releases.length > 0;
  const hasEarnings = totalEarnings > 0;

  const stats = [
    {
      name: t('dashboard.totalTracks'),
      value: userReleases.length.toString(),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10l12-3" />
        </svg>
      ),
      change: userReleases.length > 0 ? `+${userReleases.length}` : '0',
      changeType: userReleases.length > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-blue-500 to-purple-600',
    },
    {
      name: t('dashboard.published'),
      value: publishedReleases.length.toString(),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      change: publishedReleases.length > 0 ? `+${publishedReleases.length}` : '0',
      changeType: publishedReleases.length > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      name: t('dashboard.pendingApproval'),
      value: pendingReleases.length.toString(),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      change: pendingReleases.length > 0 ? `${pendingReleases.length}` : '0',
      changeType: 'neutral' as const,
      gradient: 'from-yellow-500 to-orange-600',
    },
    {
      name: t('dashboard.totalEarnings'),
      value: `₺${totalEarnings.toFixed(2)}`,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      change: totalEarnings > 0 ? `₺${totalEarnings.toFixed(2)}` : '₺0.00',
      changeType: totalEarnings > 0 ? 'increase' as const : 'neutral' as const,
      gradient: 'from-emerald-500 to-green-600',
    },
  ];

  const recentActivity = [
    {
      type: 'release',
      title: t('admin.epProjectPending'),
      time: t('admin.hoursAgo'),
      status: 'pending',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      type: 'earning',
      title: t('admin.spotifyEarning'),
      time: t('admin.dayAgo'),
      status: 'success',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      type: 'release',
      title: t('admin.firstSongPublished'),
      time: t('admin.daysAgo'),
      status: 'success',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 rounded-2xl text-white p-8 shadow-2xl">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl font-bold">
                {(artist?.display_name || user?.email)?.[0]?.toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold">
                {t('dashboard.welcome')}, {artist?.display_name || user?.email?.split('@')[0]}!
              </h1>
              <p className="text-white/80 text-lg mt-2">
                {user?.role === 'artist' 
                  ? t('dashboard.artistSubtitle')
                  : t('dashboard.adminSubtitle')
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isLoading ? (
          // Loading state
          [...Array(4)].map((_, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl"></div>
                <div className="w-16 h-6 bg-white/20 rounded-full"></div>
              </div>
              <div className="space-y-2">
                <div className="w-20 h-4 bg-white/20 rounded"></div>
                <div className="w-16 h-8 bg-white/20 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          stats.map((stat, index) => (
            <div key={stat.name} className="group">
              <div className="relative overflow-hidden bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.gradient} text-white shadow-lg`}>
                    {stat.icon}
                  </div>
                  <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                    stat.changeType === 'increase' 
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                      : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                    {stat.change}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-white/70 mb-1">{stat.name}</p>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Boş durumlar için mesajlar */}
      {!isLoading && !hasReleases && (
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 shadow-xl text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
            <MusicIcon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
                          {t('messages.noTracks')}
          </h3>
          <p className="text-white/70 mb-6">
                          {t('messages.uploadFirstTrackMessage')}
          </p>
          <Link 
            to="/tracks/new" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
                          {t('dashboard.uploadNewTrack')}
          </Link>
        </div>
      )}

      {!isLoading && hasReleases && !hasEarnings && (
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 shadow-xl text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
                          {t('earnings.noEarningsYet')}
          </h3>
          <p className="text-white/70 mb-6">
            {t('earnings.earningsWillAppear')}
          </p>
          <Link 
            to="/earnings" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {t('earnings.viewEarnings')}
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('dashboard.recentActivity')}
          </h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-200">
                <div className={`p-2 rounded-lg ${
                  activity.status === 'success' ? 'bg-green-500/20 text-green-300' :
                  activity.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-gray-500/20 text-gray-300'
                }`}>
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {activity.title}
                  </p>
                  <p className="text-xs text-white/60">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('dashboard.quickActions')}
          </h3>
          <div className="space-y-3">
            {user?.role === 'artist' ? (
              <>
                <Link to="/tracks/new" className="group flex items-center w-full p-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
{t('dashboard.uploadNewMusic')}
                </Link>
                <Link to="/earnings" className="group flex items-center w-full p-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
{t('dashboard.viewRevenueReport')}
                </Link>
                <Link to="/tracks" className="group flex items-center w-full p-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                  </svg>
{t('dashboard.myMusic')}
                </Link>
              </>
            ) : (
              <>
                <Link to="/admin/pending" className="group flex items-center w-full p-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
{t('admin.viewPendingApprovals')}
                </Link>
                <Link to="/admin/artists" className="group flex items-center w-full p-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
{t('admin.artistManagement')}
                </Link>
                <Link to="/admin/earnings" className="group flex items-center w-full p-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all duration-300">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
{t('admin.platformStatistics')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent Releases */}
      {userReleases.length > 0 && (
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 shadow-xl">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
            </svg>
            {user?.role === 'admin' ? t('admin.recentReleases') : t('dashboard.recentMusic')}
          </h3>
          <div className="space-y-4">
            {userReleases.slice(0, 3).map((release) => (
              <div key={release.id} className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-200">
                {release.cover_art ? (
                  <img 
                    src={release.cover_art} 
                    alt={release.title}
                    className="w-16 h-16 rounded-xl object-cover border border-white/20"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center border border-white/20 rounded-xl">
                    <MusicIcon size="md" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {release.title}
                  </p>
                  <p className="text-sm text-white/60">
                    {release.type} • {new Date(release.release_date).toLocaleDateString('tr-TR')}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    (release.status === 'published' || release.status === 'approved') ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                    (release.status === 'pending' || release.status === 'submitted') ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                    release.status === 'draft' ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' :
                    'bg-red-500/20 text-red-300 border border-red-500/30'
                  }`}>
                    {(release.status === 'published' || release.status === 'approved') ? t('status.published') :
                     (release.status === 'pending' || release.status === 'submitted') ? t('status.pending') :
                     release.status === 'draft' ? t('status.draft') : t('status.rejected')
                    }
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 